import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Dialog, DialogContent, Button, IconButton, Badge, useMediaQuery, useTheme, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { PlayArrow, PictureAsPdfOutlined } from '@mui/icons-material';

import { useSelector, useDispatch } from 'react-redux';
import { logout, updateUserProfile } from '../../store/slices/authSlice';
import '../../assets/styles/Home.css';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import ValEdIcon from './ValEdIcon';
import CloseIcon from '@mui/icons-material/Close';
import ordersService from '../../services/orders.service';
import { toast } from 'react-toastify';

function Header() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user, userType, authInitialized, loading: authLoading } = useSelector((state) => state.auth);
  const { totalItems } = useSelector((state) => state.cart);
  const [ordersSummary, setOrdersSummary] = useState({ total: 0, pending: 0 });
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState(userType || 'parents');
  const [savingUserType, setSavingUserType] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!authInitialized || !isAuthenticated) {
        setOrdersSummary({ total: 0, pending: 0 });
        return;
      }

      try {
        const orders = await ordersService.getMyOrders();
        const pending = orders.filter((order) => order.status === 'pending').length;
        setOrdersSummary({ total: orders.length, pending });
      } catch (error) {
        console.error('Erreur lors du chargement des commandes:', error);
      }
    };

    fetchOrders();
  }, [authInitialized, isAuthenticated]);

  // Fonction pour générer les initiales (2 lettres)
  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 4)
      .join('');
  };

  const displayName = user?.name || user?.email || 'User';

  const handleLogin = () => {
    navigate('/HomePage');
  };

  const handleProfileClick = () => {
    setSelectedUserType(user?.user_type || userType || 'parents');
    setProfileDialogOpen(true);
  };

  const handleUserTypeChange = async (event) => {
    const newType = event.target.value;
    const previousType = selectedUserType;
    if (newType === previousType || savingUserType) return;

    setSelectedUserType(newType);
    setSavingUserType(true);
    toast.dismiss('profile-update');

    try {
      await dispatch(updateUserProfile({ user_type: newType })).unwrap();
      toast.success('Profil mis à jour', {
        toastId: 'profile-update',
        theme: 'light',
        autoClose: 2500,
      });
    } catch (error) {
      setSelectedUserType(previousType);
      toast.error(error || 'Impossible de mettre à jour le profil', {
        toastId: 'profile-update',
        theme: 'light',
        autoClose: 3500,
      });
    } finally {
      setSavingUserType(false);
    }
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setProfileDialogOpen(false);
    navigate('/');
  };

  const handleBackdropClick = (event, reason) => {
    if (reason === 'backdropClick') {
      setProfileDialogOpen(false);
    }
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <header className="header" >
      <Box sx={{paddingTop:'10px', cursor:'pointer'}}   onClick={handleLogin}>
        <Logo />
      </Box>
      {!isMobile && (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => navigate('/HomePage')}
            sx={{
              color: '#12284A',
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: 'Achiko'
            }}
          >
            Accueil
          </Button>
          {/* <Button
            variant="text"
            onClick={() => navigate('/articles')}
            sx={{
              color: '#12284A',
              fontWeight: 600,
              textTransform: 'none',
              fontFamily: 'Achiko'
            }}
          >
            Shop
          </Button> */}
        </Box>
      )}
       <Box sx={{paddingTop:'20px', display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center'}}>
       {isAuthenticated && user ? (
         <>
           <IconButton
             onClick={() => navigate('/cart')}
             sx={{
               color: '#EBA45B',
               mr: { xs: 0.5, sm: 2 },
               p: { xs: 1, sm: 1.5 },
               '&:hover': {
                 bgcolor: 'rgba(67, 172, 217, 0.1)'
               }
             }}
           >
             <Badge badgeContent={totalItems} color="error">
              <ValEdIcon size={24} color="#EBA45B" />
            </Badge>
          </IconButton>

           <Avatar
            onClick={handleProfileClick}
            sx={{
              bgcolor: '#784e3c',
              cursor: 'pointer',
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              fontSize: { xs: '0.75rem', sm: '0.9rem' },
              fontWeight: 'bold'
            }}
          >
             {getInitials(displayName)}
          </Avatar>

           {/* Dialog du profil */}
           <Dialog
             open={profileDialogOpen}
             onClose={handleBackdropClick}
             maxWidth="xs"
             fullWidth
             PaperProps={{
               sx: {
                 borderRadius: 2,
                 padding: 1
               }
             }}
           >
             <DialogContent sx={{ textAlign: 'center', padding: '20px !important' }}>
               <IconButton
                 onClick={handleProfileDialogClose}
                 sx={{ position: 'absolute', right: 8, top: 8 }}
               >
                 <CloseIcon />
               </IconButton>

               <Avatar
                 sx={{
                   bgcolor: '#784e3c',
                   width: 60,
                   height: 60,
                   fontSize: '1.2rem',
                   fontWeight: 'bold',
                   margin: '0 auto 20px auto'
                 }}
               >
                  {getInitials(displayName)}
               </Avatar>

               <Typography variant="h6" gutterBottom>
                 Profil utilisateur
               </Typography>

               {user.name && (
                 <Typography variant="body1" sx={{ marginBottom: 0.5, fontWeight: 600 }}>
                   {user.name}
                 </Typography>
               )}

               <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
                 {user.email}
               </Typography>

               <FormControl fullWidth sx={{ marginBottom: 3 }} disabled={savingUserType || authLoading}>
                 <InputLabel id="user-type-label">Vous êtes</InputLabel>
                 <Select
                   labelId="user-type-label"
                   id="user-type-select"
                   value={selectedUserType}
                   label="Vous êtes"
                   onChange={handleUserTypeChange}
                   sx={{
                     '& .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#EBA45B'
                     },
                     '&:hover .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#EBA45B'
                     },
                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                       borderColor: '#EBA45B',
                       borderWidth: 2
                     }
                   }}
                 >
                   <MenuItem value="parents">Parents</MenuItem>
                   <MenuItem value="enseignants">Enseignants</MenuItem>
                 </Select>
               </FormControl>

                {(ordersSummary.total > 0) ? (
                  <Badge 
                    badgeContent={ordersSummary.pending > 0 ? ordersSummary.pending : ordersSummary.total}
                    color={ordersSummary.pending > 0 ? 'error' : 'primary'}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ width: 'fit-content' }}
                  >
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ marginBottom: 3 , cursor:'pointer' }} 
                      onClick={() => navigate('/orders')}
                    >
                      mes commandes 
                    </Typography>
                  </Badge>
                ) : (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ marginBottom: 3 , cursor:'pointer' }} 
                    onClick={() => navigate('/orders')}
                  >
                    mes commandes 
                  </Typography>
                )}


               <Button
                 variant="contained"
                 color="error"
                 onClick={handleLogout}
                 fullWidth
                 sx={{ marginTop: 2 }}
               >
                 Se déconnecter
               </Button>
             </DialogContent>
           </Dialog>
         </>
       ) : (
         <a href="/auth" className="login-intent-btn"> Se connecter </a>
       )}
       </Box>
    </header>
  )
}

export default Header